import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { EyeIcon, MagnifyingGlassIcon, PlusIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  FormCard,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  QUANTITY_STEP,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import {
  useActiveWarehouses,
  useCancelGoodsReceipt,
  useCreateGoodsReceipt,
  useGoodsReceipt,
  useGoodsReceiptsPage,
  useGoodsReceiptSummary,
  usePendingOrder,
  useReceivableOrders,
} from '../../hooks/useWarehouses';
import { GoodsReceiptInput } from '../../services/warehouse.service';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StatusBadge: React.FC<{ status: 'posted' | 'cancelled' }> = ({ status }) => (
  <span className={cn(badgeClass, status === 'posted' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>
    {status === 'posted' ? 'Received' : 'Cancelled'}
  </span>
);

interface LineState {
  received: string;
  rejected: string;
  rejectionReason: string;
  batchNumber: string;
  heatNumber: string;
  millTcNumber: string;
}
const emptyLine: LineState = { received: '', rejected: '', rejectionReason: '', batchNumber: '', heatNumber: '', millTcNumber: '' };

interface ReceiptFormProps {
  initialOrderId: string;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: GoodsReceiptInput) => void;
}

const ReceiptForm: React.FC<ReceiptFormProps> = ({ initialOrderId, isPending, onCancel, onSubmit }) => {
  const [orderId, setOrderId] = useState(initialOrderId);
  const { data: orders = [], isLoading: ordersLoading } = useReceivableOrders(true);
  const { data: order, isLoading: orderLoading } = usePendingOrder(orderId || undefined);
  const { data: warehouses = [] } = useActiveWarehouses();

  const [header, setHeader] = useState({ warehouseId: '', receiptDate: today(), vendorChallanNumber: '', vendorInvoiceNumber: '', vehicleNumber: '', notes: '' });
  const [lines, setLines] = useState<Record<string, LineState>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Goods go into the default warehouse unless another is chosen
  useEffect(() => {
    if (!header.warehouseId && warehouses.length > 0) {
      setHeader((prev) => ({ ...prev, warehouseId: warehouses.find((warehouse) => warehouse.isDefault)?.id ?? warehouses[0].id }));
    }
  }, [header.warehouseId, warehouses]);

  // Another order has other lines
  useEffect(() => {
    setLines({});
    setErrors({});
  }, [orderId]);

  const pending = (order?.lines ?? []).filter((line) => line.pendingQuantity > 0);
  const lineOf = (id: string) => lines[id] ?? emptyLine;
  const setLine = (id: string, field: keyof LineState, value: string) => {
    setLines((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyLine), [field]: value } }));
    setErrors((prev) => {
      const { [`line-${id}`]: _cleared, lines: _all, ...rest } = prev;
      return rest;
    });
  };
  const setHeaderField = (field: keyof typeof header) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setHeader((prev) => ({ ...prev, [field]: event.target.value }));

  const receiveAll = () =>
    setLines((prev) => Object.fromEntries(pending.map((line) => [line.purchaseOrderItemId, { ...(prev[line.purchaseOrderItemId] ?? emptyLine), received: String(line.pendingQuantity) }])));

  const totals = pending.reduce(
    (sum, line) => {
      const state = lineOf(line.purchaseOrderItemId);
      const received = Number(state.received) || 0;
      const rejected = Number(state.rejected) || 0;
      return {
        received: Math.round((sum.received + received) * 10000) / 10000,
        accepted: Math.round((sum.accepted + Math.max(0, received - rejected)) * 10000) / 10000,
        value: sum.value + Math.max(0, received - rejected) * line.unitPrice,
      };
    },
    { received: 0, accepted: 0, value: 0 },
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!orderId) next.orderId = 'Choose the purchase order the goods came against';
    if (!header.warehouseId) next.warehouseId = 'Choose where the goods are put';
    if (!header.receiptDate) next.receiptDate = 'Enter the receipt date';
    else if (header.receiptDate > today()) next.receiptDate = 'The receipt date cannot be in the future';

    const items: GoodsReceiptInput['items'] = [];
    pending.forEach((line) => {
      const state = lineOf(line.purchaseOrderItemId);
      if (state.received.trim() === '' || Number(state.received) === 0) return;
      const received = Number(state.received);
      const rejected = state.rejected.trim() === '' ? 0 : Number(state.rejected);
      const key = `line-${line.purchaseOrderItemId}`;
      if (!isQuantity(received) || received < 0) next[key] = 'Received can have up to 4 decimals';
      else if (!isQuantity(rejected) || rejected < 0) next[key] = 'Rejected can have up to 4 decimals';
      else if (rejected > received) next[key] = 'Rejected cannot be more than received';
      else if (Math.round((received - rejected) * 10000) / 10000 > line.pendingQuantity) next[key] = `Only ${formatNumber(line.pendingQuantity)} are still to come`;
      else if (rejected > 0 && !state.rejectionReason.trim()) next[key] = 'Say why it is rejected';
      else
        items.push({
          purchaseOrderItemId: line.purchaseOrderItemId,
          receivedQuantity: received,
          rejectedQuantity: rejected,
          rejectionReason: rejected > 0 ? state.rejectionReason.trim() : null,
          batchNumber: state.batchNumber.trim() || null,
          heatNumber: state.heatNumber.trim() || null,
          millTcNumber: state.millTcNumber.trim() || null,
        });
    });
    if (orderId && items.length === 0 && !Object.keys(next).some((key) => key.startsWith('line-'))) next.lines = 'Enter the quantity received for at least one line';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      purchaseOrderId: orderId,
      warehouseId: header.warehouseId,
      receiptDate: header.receiptDate,
      vendorChallanNumber: header.vendorChallanNumber.trim() || null,
      vendorInvoiceNumber: header.vendorInvoiceNumber.trim() || null,
      vehicleNumber: header.vehicleNumber.trim() || null,
      notes: header.notes.trim() || null,
      items,
    });
  };

  if (!ordersLoading && orders.length === 0 && !initialOrderId) {
    return (
      <div className="py-6 text-center">
        <ShoppingCartIcon className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm font-medium text-gray-900">No purchase order is waiting for goods</p>
        <p className="mt-1 text-sm text-gray-500">Goods are received against an approved purchase order.</p>
        <Link to="/purchase" className={cn(primaryButtonClass, 'mt-4')}>
          Go to Purchase Orders
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <FormCard title="Delivery" description="Which order the goods came against, and the papers that came with them">
        <Field label="Purchase order" htmlFor="grn-order" required error={errors.orderId} className="sm:col-span-2">
          <select id="grn-order" value={orderId} onChange={(event) => setOrderId(event.target.value)} className={cn(inputClass, errors.orderId && inputErrorClass)}>
            <option value="">Select…</option>
            {/* An order opened from the purchase list is offered even before the list has loaded */}
            {orderId && order && !orders.some((candidate) => candidate.id === orderId) && <option value={orderId}>{order.orderNumber} · {order.vendorName}</option>}
            {orders.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.orderNumber} · {candidate.vendorName ?? 'No vendor'} · {formatNumber(candidate.pendingQuantity)} to come
              </option>
            ))}
          </select>
        </Field>
        <Field label="Receipt date" htmlFor="grn-date" required error={errors.receiptDate}>
          <input id="grn-date" type="date" max={today()} value={header.receiptDate} onChange={setHeaderField('receiptDate')} className={cn(inputClass, errors.receiptDate && inputErrorClass)} />
        </Field>
        <Field label="Put into warehouse" htmlFor="grn-warehouse" required error={errors.warehouseId}>
          <select id="grn-warehouse" value={header.warehouseId} onChange={setHeaderField('warehouseId')} className={cn(inputClass, errors.warehouseId && inputErrorClass)}>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name} ({warehouse.code})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Vendor challan no." htmlFor="grn-challan">
          <input id="grn-challan" type="text" maxLength={50} value={header.vendorChallanNumber} onChange={setHeaderField('vendorChallanNumber')} className={inputClass} />
        </Field>
        <Field label="Vendor invoice no." htmlFor="grn-invoice">
          <input id="grn-invoice" type="text" maxLength={50} value={header.vendorInvoiceNumber} onChange={setHeaderField('vendorInvoiceNumber')} className={inputClass} />
        </Field>
        <Field label="Vehicle no." htmlFor="grn-vehicle">
          <input id="grn-vehicle" type="text" maxLength={30} value={header.vehicleNumber} onChange={setHeaderField('vehicleNumber')} className={inputClass} />
        </Field>
        <Field label="Notes" htmlFor="grn-notes">
          <input id="grn-notes" type="text" maxLength={1000} value={header.notes} onChange={setHeaderField('notes')} className={inputClass} />
        </Field>
      </FormCard>

      {orderId && (
        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">What arrived</h4>
              <p className="mt-0.5 text-xs text-gray-500">Leave a line empty if nothing came. Only the accepted quantity enters stock.</p>
            </div>
            {pending.length > 0 && (
              <button type="button" onClick={receiveAll} className="self-start rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50 sm:self-auto">
                Fill everything still to come
              </button>
            )}
          </div>
          {orderLoading ? (
            <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>
          ) : order && !order.receivable ? (
            <p className="px-4 py-6 text-sm text-gray-700">
              {order.orderNumber} is {order.status.replace('_', ' ')}; goods cannot be received against it.
            </p>
          ) : pending.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500">Everything on this order has been received.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {pending.map((line) => {
                const state = lineOf(line.purchaseOrderItemId);
                const error = errors[`line-${line.purchaseOrderItemId}`];
                const received = Number(state.received) || 0;
                const rejected = Number(state.rejected) || 0;
                return (
                  <div key={line.purchaseOrderItemId} className="space-y-3 p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{line.productName}</p>
                        <p className="text-xs text-gray-500">{line.sku}</p>
                      </div>
                      <p className="text-xs text-gray-600 tabular-nums">
                        Ordered {formatNumber(line.orderedQuantity)} · received {formatNumber(line.receivedQuantity)} ·{' '}
                        <span className="font-semibold text-gray-900">{formatNumber(line.pendingQuantity)} {line.unit} to come</span>
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
                      <Field label="Received" htmlFor={`rec-${line.purchaseOrderItemId}`}>
                        <input
                          id={`rec-${line.purchaseOrderItemId}`}
                          type="number"
                          min="0"
                          step={QUANTITY_STEP}
                          value={state.received}
                          onChange={(event) => setLine(line.purchaseOrderItemId, 'received', event.target.value)}
                          aria-label={`Received quantity of ${line.productName}`}
                          className={cn(inputClass, 'text-right tabular-nums', error && inputErrorClass)}
                        />
                      </Field>
                      <Field label="Rejected" htmlFor={`rej-${line.purchaseOrderItemId}`}>
                        <input
                          id={`rej-${line.purchaseOrderItemId}`}
                          type="number"
                          min="0"
                          step={QUANTITY_STEP}
                          value={state.rejected}
                          onChange={(event) => setLine(line.purchaseOrderItemId, 'rejected', event.target.value)}
                          aria-label={`Rejected quantity of ${line.productName}`}
                          className={cn(inputClass, 'text-right tabular-nums')}
                        />
                      </Field>
                      <Field label="Accepted">
                        <p className="rounded-md bg-gray-50 px-3 py-2 text-right text-sm font-semibold text-gray-900 tabular-nums ring-1 ring-gray-200">
                          {formatNumber(Math.max(0, Math.round((received - rejected) * 10000) / 10000))}
                        </p>
                      </Field>
                      <Field label="Batch / lot no." htmlFor={`bat-${line.purchaseOrderItemId}`}>
                        <input id={`bat-${line.purchaseOrderItemId}`} type="text" maxLength={50} value={state.batchNumber} onChange={(event) => setLine(line.purchaseOrderItemId, 'batchNumber', event.target.value)} className={inputClass} />
                      </Field>
                      <Field label="Heat no." htmlFor={`heat-${line.purchaseOrderItemId}`}>
                        <input id={`heat-${line.purchaseOrderItemId}`} type="text" maxLength={50} value={state.heatNumber} onChange={(event) => setLine(line.purchaseOrderItemId, 'heatNumber', event.target.value)} className={inputClass} />
                      </Field>
                      <Field label="Mill TC no." htmlFor={`tc-${line.purchaseOrderItemId}`}>
                        <input id={`tc-${line.purchaseOrderItemId}`} type="text" maxLength={50} value={state.millTcNumber} onChange={(event) => setLine(line.purchaseOrderItemId, 'millTcNumber', event.target.value)} className={inputClass} />
                      </Field>
                      {rejected > 0 && (
                        <Field label="Reason for rejection" htmlFor={`why-${line.purchaseOrderItemId}`} required className="col-span-2 lg:col-span-6">
                          <input
                            id={`why-${line.purchaseOrderItemId}`}
                            type="text"
                            maxLength={300}
                            value={state.rejectionReason}
                            onChange={(event) => setLine(line.purchaseOrderItemId, 'rejectionReason', event.target.value)}
                            aria-label={`Reason for rejecting ${line.productName}`}
                            className={inputClass}
                            placeholder="For example damaged, wrong grade, short length"
                          />
                        </Field>
                      )}
                    </div>
                    {error && <p className="text-sm text-red-600">{error}</p>}
                  </div>
                );
              })}
              <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-4 py-3 text-sm">
                <span className="text-gray-600">
                  Received {formatNumber(totals.received)} · accepted <span className="font-semibold text-gray-900">{formatNumber(totals.accepted)}</span>
                </span>
                <span className="font-semibold text-gray-900 tabular-nums">{formatMoney(totals.value)} into stock</span>
              </div>
            </div>
          )}
          {errors.lines && <p className="border-t border-gray-200 px-4 py-3 text-sm text-red-600">{errors.lines}</p>}
        </section>
      )}

      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending || !orderId || pending.length === 0} className={primaryButtonClass}>
          {isPending ? 'Saving…' : 'Receive Goods'}
        </button>
      </FormActions>
    </form>
  );
};

// One receipt with its lines; a posted receipt can be cancelled with a reason
const ReceiptView: React.FC<{ receiptId: string; canCancel: boolean; onClose: () => void }> = ({ receiptId, canCancel, onClose }) => {
  const { data: receipt, isLoading, isError, error } = useGoodsReceipt(receiptId);
  const cancelReceipt = useCancelGoodsReceipt();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !receipt) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The goods receipt could not be loaded.')}</p>;

  const items = receipt.items ?? [];
  const handleCancel = () => {
    cancelReceipt.mutate(
      { id: receipt.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${receipt.grnNumber} cancelled`);
          setCancelling(false);
        },
        onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The goods receipt could not be cancelled')),
      },
    );
  };

  return (
    <div className="space-y-5">
      <DetailCard title="Receipt" columns={4}>
        <DetailItem label="Purchase order">{receipt.purchaseOrderNumber}</DetailItem>
        <DetailItem label="Vendor">{receipt.vendorName || '—'}</DetailItem>
        <DetailItem label="Receipt date">{formatDate(receipt.receiptDate)}</DetailItem>
        <DetailItem label="Status"><StatusBadge status={receipt.status} /></DetailItem>
        <DetailItem label="Warehouse">{receipt.warehouseName || '—'}</DetailItem>
        <DetailItem label="Vendor challan">{receipt.vendorChallanNumber || '—'}</DetailItem>
        <DetailItem label="Vendor invoice">{receipt.vendorInvoiceNumber || '—'}</DetailItem>
        <DetailItem label="Vehicle">{receipt.vehicleNumber || '—'}</DetailItem>
        <DetailItem label="Accepted value">{formatMoney(receipt.acceptedValue)}</DetailItem>
        <DetailItem label="Notes" className="col-span-2 lg:col-span-3">
          <span className="font-normal">{receipt.notes || '—'}</span>
        </DetailItem>
        {receipt.status === 'cancelled' && (
          <DetailItem label="Cancelled because" className="col-span-2 lg:col-span-4">
            <span className="font-normal text-red-700">{receipt.cancelReason || '—'}</span>
          </DetailItem>
        )}
      </DetailCard>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Product</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Received</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Accepted</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Rejected</th>
              <th scope="col" className={thClass}>Batch / lot</th>
              <th scope="col" className={thClass}>Heat no.</th>
              <th scope="col" className={thClass}>Mill TC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2.5">
                  <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                  <p className="text-xs text-gray-500">{item.sku}</p>
                  {item.rejectionReason && <p className="mt-0.5 text-xs text-red-600">Rejected: {item.rejectionReason}</p>}
                  {item.inspectionStatus === 'pending' && <p className="mt-0.5 text-xs font-medium text-amber-700">Held for inspection · {item.inspectionLotNumber}</p>}
                  {item.inspectionStatus && item.inspectionStatus !== 'pending' && <p className="mt-0.5 text-xs text-gray-500">Inspected · {item.inspectionLotNumber}</p>}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(item.receivedQuantity)} <span className="text-gray-500">{item.unit}</span></td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(item.acceptedQuantity)}</td>
                <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', item.rejectedQuantity > 0 ? 'font-medium text-red-600' : 'text-gray-400')}>
                  {item.rejectedQuantity > 0 ? formatNumber(item.rejectedQuantity) : '—'}
                </td>
                <td className="px-4 py-2.5 text-sm text-gray-700">{item.batchNumber || '—'}</td>
                <td className="px-4 py-2.5 text-sm text-gray-700">{item.heatNumber || '—'}</td>
                <td className="px-4 py-2.5 text-sm text-gray-700">{item.millTcNumber || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cancelling && (
        <div className="space-y-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-600/20">
          <p className="text-sm text-red-800">
            The {formatNumber(receipt.acceptedQuantity)} accepted unit(s) leave {receipt.warehouseName} again and {receipt.purchaseOrderNumber} expects them once
            more. This is refused if the goods have already been used or sold.
          </p>
          <Field label="Reason" htmlFor="grn-cancel-reason" required>
            <input id="grn-cancel-reason" type="text" maxLength={300} autoFocus value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
        </div>
      )}

      <FormActions>
        {cancelling ? (
          <>
            <button type="button" onClick={() => setCancelling(false)} disabled={cancelReceipt.isPending} className={secondaryButtonClass}>
              Back
            </button>
            <button type="button" onClick={handleCancel} disabled={cancelReceipt.isPending || reason.trim().length < 3} className={dangerButtonClass}>
              {cancelReceipt.isPending ? 'Cancelling…' : 'Cancel Receipt'}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onClose} className={secondaryButtonClass}>
              Close
            </button>
            {receipt.status === 'posted' && canCancel && (
              <button type="button" onClick={() => setCancelling(true)} className={secondaryButtonClass}>
                Cancel Receipt
              </button>
            )}
          </>
        )}
      </FormActions>
    </div>
  );
};

const GoodsReceipts: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('purchase.create');
  const canCancel = can('purchase.update');
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'posted' | 'cancelled' | ''>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);

  // "Receive Goods" on a purchase order opens the form with that order chosen
  const orderFromLink = searchParams.get('po') ?? '';
  const [formOpen, setFormOpen] = useState(!!orderFromLink);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching, refetch } = useGoodsReceiptsPage({ page, limit, search: search || undefined, status: statusFilter });
  const { data: summary } = useGoodsReceiptSummary();
  const createReceipt = useCreateGoodsReceipt();

  const receipts = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const viewing = receipts.find((receipt) => receipt.id === viewingId);

  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const hasFilters = !!searchInput || !!statusFilter;
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  const closeForm = () => {
    if (createReceipt.isPending) return;
    setFormOpen(false);
    if (orderFromLink) setSearchParams({}, { replace: true });
  };

  const handleSubmit = (input: GoodsReceiptInput) => {
    createReceipt.mutate(input, {
      onSuccess: (saved) => {
        toast.success(`${saved.grnNumber}: ${formatNumber(saved.acceptedQuantity)} unit(s) added to stock`);
        setFormOpen(false);
        if (orderFromLink) setSearchParams({}, { replace: true });
        setViewingId(saved.id);
      },
      onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The goods receipt could not be saved')),
    });
  };

  const stats = [
    { label: 'Receipts', value: summary ? formatNumber(summary.posted) : '—', hint: `${summary ? formatNumber(summary.cancelled) : '—'} cancelled` },
    { label: 'This month', value: summary ? formatNumber(summary.thisMonth) : '—', hint: 'Receipts booked' },
    { label: 'Orders awaiting goods', value: summary ? formatNumber(summary.ordersAwaitingReceipt) : '—', hint: `${summary ? formatNumber(summary.unitsAwaitingReceipt) : '—'} unit(s) still to come` },
    { label: 'Rejected', value: summary ? formatNumber(summary.rejectedUnits) : '—', hint: 'Unit(s) refused at receipt' },
  ];

  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Goods Receipts</h1>
          <p className="mt-1 text-sm text-gray-500">
            What arrived against purchase orders, delivery by delivery. Accepted goods enter stock; rejected goods do not.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/purchase" className={secondaryButtonClass}>
            <ShoppingCartIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Purchase Orders
          </Link>
          {canCreate && (
            <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              Receive Goods
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by GRN, order, vendor, product, challan, batch or heat number"
              aria-label="Search goods receipts"
              className={cn(inputClass, 'pl-9')}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as typeof statusFilter);
              setPage(1);
            }}
            aria-label="Status"
            className={cn(inputClass, 'lg:w-44')}
          >
            <option value="">All statuses</option>
            <option value="posted">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              Clear
            </button>
          )}
        </div>

        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The goods receipts could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : (
          <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>GRN</th>
                  <th scope="col" className={thClass}>Purchase order</th>
                  <th scope="col" className={thClass}>Vendor</th>
                  <th scope="col" className={thClass}>Warehouse</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Accepted</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Rejected</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Value</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 9 }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : receipts.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No goods receipt matches' : 'No goods receipts yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">
                        {hasFilters ? 'Try a different search or status.' : 'Receive goods against an approved purchase order.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  receipts.map((receipt) => (
                    <tr key={receipt.id} className={cn('hover:bg-gray-50', receipt.status === 'cancelled' && 'bg-gray-50/60')}>
                      <td className="whitespace-nowrap px-4 py-3">
                        <button type="button" onClick={() => setViewingId(receipt.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                          {receipt.grnNumber}
                        </button>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDate(receipt.receiptDate)}</p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{receipt.purchaseOrderNumber}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{receipt.vendorName || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{receipt.warehouseName || '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(receipt.acceptedQuantity)}</td>
                      <td className={cn('whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums', receipt.rejectedQuantity > 0 ? 'font-medium text-red-600' : 'text-gray-400')}>
                        {receipt.rejectedQuantity > 0 ? formatNumber(receipt.rejectedQuantity) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMoney(receipt.acceptedValue)}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <StatusBadge status={receipt.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setViewingId(receipt.id)}
                          title="View"
                          aria-label={`View ${receipt.grnNumber}`}
                          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>
              Showing {firstRow}–{lastRow} of {formatNumber(total)}
            </span>
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} title="Receive Goods" description="Books a goods receipt note (GRN). Accepted quantities are added to stock straight away." size="2xl">
        {formOpen && <ReceiptForm initialOrderId={orderFromLink} isPending={createReceipt.isPending} onCancel={closeForm} onSubmit={handleSubmit} />}
      </Modal>

      <Modal isOpen={!!viewingId && !formOpen} onClose={() => setViewingId(null)} title={viewing?.grnNumber ?? 'Goods Receipt'} size="2xl">
        {viewingId && !formOpen && <ReceiptView key={viewingId} receiptId={viewingId} canCancel={canCancel} onClose={() => setViewingId(null)} />}
      </Modal>
    </div>
  );
};

export default GoodsReceipts;
